import type { FormAnswerMap } from "../pageLogic";

/** Score engines a screener can work out in the browser. Others run only in the database after an entry is saved. */
export const BROWSER_SCORE_ENGINES = ["sum_total", "sum_subscales"] as const;

export interface ScoreSourceInput {
    input_id: string;
    input_key: string | null;
    page_index: number;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function numericAnswer(value: FormAnswerMap[string] | undefined): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
    return null;
}

function sumAnswers(inputs: ScoreSourceInput[], answers: FormAnswerMap) {
    let total = 0;
    let answered = 0;
    for (const input of inputs) {
        const value = numericAnswer(answers[input.input_id]);
        if (value == null) continue;
        total += value;
        answered += 1;
    }
    return { total, answered };
}

function minAnswered(config: Record<string, unknown>): number | null {
    const raw = Number(config.min_answered);
    return config.min_answered == null || !Number.isFinite(raw) ? null : raw;
}

/**
 * The calculated scores for a set of answers, keyed by output input id. Mirrors the database calculators
 * calculate_sum_total_score_for_entry and calculate_sum_subscales_score_for_entry.
 */
export function computeCalculatedScores(
    scoreEngine: string | null | undefined,
    scoreEngineConfig: unknown,
    inputs: ScoreSourceInput[],
    answers: FormAnswerMap
): Record<string, number> {
    const config = asRecord(scoreEngineConfig);

    if (scoreEngine === "sum_total") {
        const outputId = typeof config.output_input_id === "string" ? config.output_input_id : null;
        if (!outputId) return {};
        const keys = Array.isArray(config.source_input_keys) ? config.source_input_keys.map(String) : ["scale-rating"];
        const { total, answered } = sumAnswers(
            inputs.filter((input) => keys.includes(input.input_key ?? "")),
            answers
        );
        const min = minAnswered(config);
        return min != null && answered < min ? {} : { [outputId]: total };
    }

    if (scoreEngine === "sum_subscales") {
        const scores: Record<string, number> = {};
        let total = 0;
        const subscales = Array.isArray(config.subscales) ? config.subscales.map(asRecord) : [];
        for (const subscale of subscales) {
            const outputId = typeof subscale.output_input_id === "string" ? subscale.output_input_id : null;
            const pageIndex = Number(subscale.page_index);
            if (!outputId || !Number.isFinite(pageIndex)) continue;
            const result = sumAnswers(
                inputs.filter(
                    (input) => input.page_index === pageIndex && !["number", "warning"].includes(input.input_key ?? "")
                ),
                answers
            );
            const min = minAnswered(subscale);
            if (min != null && result.answered < min) continue;
            scores[outputId] = result.total;
            total += result.total;
        }
        const totalId = typeof config.total_output_input_id === "string" ? config.total_output_input_id : null;
        if (totalId) scores[totalId] = total;
        return scores;
    }

    return {};
}
